import { Injectable } from '@angular/core';
import { signIn, signOut, getCurrentUser, fetchAuthSession, AuthUser, updateUserAttribute, SignInOutput, confirmSignIn } from 'aws-amplify/auth';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import type { SignInInput } from '@aws-amplify/auth/cognito';
import { BehaviorSubject, from, Observable, of } from 'rxjs';
import { catchError, finalize, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { AppStateService } from 'shared/services/app-state/app-state.service';
import { GenericResponse } from 'shared/models/global';
import { ApiService } from 'shared/services/api/api.service';
import { APOLLO_USER_GROUPS_KEYS, UserTypes, userPermissions } from '../../models/user';
import { GtmService } from '../gtm/gtm.service';
import { Router } from '@angular/router';

export interface AuthState {
  isLoggedIn: boolean;
  id?: string;
  username?: string;
  groups?: APOLLO_USER_GROUPS_KEYS[];
  sevenDigitalUserId?: string;
  facilityId?: string;
  organisationId?: string;
  deviceId?: string;
  userType?: UserTypes;
}

const STORAGE_RESPARKE_TOKEN_ID_KEY = 'resparke_tokenId'

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  readonly initialAuthState: AuthState = {
    isLoggedIn: false,
  };

  private readonly _authState = new BehaviorSubject<AuthState>({ ...this.initialAuthState });
  readonly authState$ = this._authState.asObservable();

  set authState(authState: AuthState) {
    this._authState.next(authState);
  }

  get authState() {
    return this._authState.value;
  }

  constructor(
    private appState: AppStateService,
    // private timeoutService: TimeoutService,
    private gtmService: GtmService,
    private apiService: ApiService,
    private router: Router,
  ) {


    cognitoUserPoolsTokenProvider.setClientMetadataProvider(async () => {
      const currentTokenId = window.sessionStorage.getItem(STORAGE_RESPARKE_TOKEN_ID_KEY);
      if (currentTokenId) {
        return { tokenId: currentTokenId };
      }
      return {};
    });
  }


  customChallengeLogin(input: { tokenId: string, challenge: string, username: string, removeChallenge?: boolean }): Observable<{ status: string }> {
    const { tokenId, challenge, username, removeChallenge } = input;

    if (tokenId) {
      // persist tokenId in sessionStorage so it can be passed in during background tokenRefresh
      window.sessionStorage.setItem(STORAGE_RESPARKE_TOKEN_ID_KEY, tokenId);
    }

    return this.programmaticLogout({ clearState: true }).pipe(
      switchMap(() => this.signIn({ username, options: { clientMetadata: { tokenId } } })
        .pipe(
          switchMap(res => {
            if (res === 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE') {
              return from(confirmSignIn({ challengeResponse: challenge, options: { clientMetadata: { tokenId } } }))
                .pipe(
                  map(res => res.nextStep.signInStep)
                );
            } else {
              return of(res);
            }
          }),
          switchMap(status => {
            if (status === 'DONE') {
              return this.getAuthSession()
            }
            return undefined;
          }),
          map(authSession => {
            if (authSession.tokens) {
              const { idToken, accessToken } = authSession.tokens;
              const accessTokenPayload = accessToken.payload;
              const idTokenPayload = idToken.payload;
              this.setAuthState(idTokenPayload, accessTokenPayload);

              if (removeChallenge) {
                // remove challenge (self closing Observable)
                this.updateUserAttribute({
                  attributeKey: 'custom:authChallenge',
                  value: ''
                }).subscribe();
              }
              return { status: 'SUCCESS' }
            }
            return { status: 'FAIL' }
          }),
          catchError(error => {
            console.log(error);
            return of({
              status: 'ERROR'
            });
          })
        )))
  }


  verifyToken(input: { token: string }) {
    this.appState.initState();
    const statement = `
      query verifyAccessToken($input: VerifyAccessTokenInput!) {
        verifyAccessToken(input: $input) {
          status
          message
          payload
        }
      }
    `;
    return this.apiService
      .graphql<GenericResponse>({ statement, variables: { input }, type: 'verifyAccessToken', iam: true })
  }


  programmaticLogout(params?: { clearState: boolean }) {
    const { clearState = true } = params || {};
    return this.signOut().pipe(
      tap(val => {
        if (clearState) {
          this.appState.clearState();
        }
      })
    );
  }

  logout() {
    return this.signOut().pipe(
      tap(() => {
        this.appState.clearState();
        this.authState = { ...this.initialAuthState };
        // this.timeoutService.stopTimer();
        this.gtmService.pushEvent({
          event: 'logout'
        });
      }),
      map(val => true)
    )
  }

  /**
   * This logout will remove the custom:authChallenge first before calling the regular logout
   */
  logoutFamily(removeToken = false) {
    return this.getCurrentUser()
      .pipe(
        switchMap(res => {
          const { user } = res;
          if (user && removeToken) {
            return this.updateUserAttribute({ attributeKey: 'custom:authChallenge', value: '' });
          }
          return of(res);
        }),
        switchMap(() => this.logout()),
      )
  }


  /**
   * This function checks if the cognito session is still active and pushes a next authState object.
   * Do NOT call this function outside the `auth.guard`, it can cause a authState conflict.
   */
  isLoggedIn(): Observable<AuthState | null> {
    return this.getAuthSession()
      .pipe(
        map(authSession => {
          if (authSession.tokens) {
            const { idToken, accessToken } = authSession.tokens;
            const idTokenPayload = idToken.payload;
            const accessTokenPayload = accessToken.payload;
            this.setAuthState(idTokenPayload, accessTokenPayload);
            return this.authState;
          } else {
            return { ...this.initialAuthState };
          }
        })
      );
  }

  hasGroupAccess(groups: any): boolean {
    if (this.authState.groups && Array.isArray(groups)) {
      const match = this.authState.groups.filter(group => {
        return groups.indexOf(group) !== -1;
      });
      if (!match.length) {
        return false
      }
      return true;
    }
    return false;
  }



  private setAuthState(idTokenpayload: any, accessTokenPayload: any): void {

    const groups = idTokenpayload['cognito:groups'];
    const userType = groups.includes(UserTypes.FAMILY) ?
      UserTypes.FAMILY : undefined;

    const authState: AuthState = {
      isLoggedIn: true,
      username: idTokenpayload['cognito:username'],
      id: idTokenpayload.sub,
      groups,
      deviceId: accessTokenPayload.device_key,
      userType,
      facilityId: idTokenpayload.facilityId,
    };

    if (idTokenpayload.residentId) {
      this.appState.set('currentResidentId', idTokenpayload.residentId);
    } else {
      console.error('Resident ID missing in IdToken');
      this.logout().subscribe({
        next: () => this.router.navigate(['/expired'])
      });
      return;
    }
    if (idTokenpayload.organisationId) {
      authState.organisationId = idTokenpayload.organisationId;
      this.appState.set('organisationId', idTokenpayload.organisationId);
    }

    if (idTokenpayload.facilityId) {
      authState.facilityId = idTokenpayload.facilityId
      this.appState.set('currentFacilityId', idTokenpayload.facilityId);
    } else {
      console.error('Facility ID missing in IdToken');
      this.logout().subscribe({
        next: () => this.router.navigate(['/expired'])
      });
      return;
    }


    this.appState.set('currentUser', {
      id: authState.id!,
      username: authState.username!,
      userType,
      permissions: groups as userPermissions[],
    });
    // push the authstate because someone might have refreshed the browser and then the authstate
    // would be in its initial state
    this.authState = authState;
  }


  // NEW FUNCTIONS


  private signIn({ username, password, options }: loginInput): Observable<string> {
    let signInRes: Observable<SignInOutput>;
    if (password) {
      signInRes = from(signIn({ username, password, options }))
    } else {
      signInRes = from(signIn({
        username, options: {
          authFlowType: 'CUSTOM_WITHOUT_SRP',
          ...options,
        }
      }))
    }
    return signInRes
      .pipe(
        map(res => res.nextStep.signInStep),
        catchError((error: any) => {
          let response = 'UNKNOWN_ERROR';
          if (error.message === 'There is already a signed in user.') {
            return of('ALREADY_LOGGED_IN');
          }
          return of(response);
        })
      )
  }

  private authSession$: Observable<Awaited<ReturnType<typeof fetchAuthSession>>> | null = null;

  /**
   * @description Get the current Cognito AuthSession. Returns object with empty values if not authenticated.
   * Concurrent callers (e.g. AuthGuard's canActivate + canActivateChild firing on the same navigation)
   * share one in-flight fetchAuthSession() call instead of each triggering their own Cognito Identity
   * Pool round trip — that duplication was causing GetCredentialsForIdentity throttling.
   * @returns Observable AuthSession
   */
  private getAuthSession() {
    if (!this.authSession$) {
      this.authSession$ = from(fetchAuthSession()).pipe(
        shareReplay({ bufferSize: 1, refCount: true }),
        finalize(() => this.authSession$ = null),
      );
    }
    return this.authSession$;
  }

  private signOut() {
    return from(signOut());
  }

  private updateUserAttribute({ attributeKey, value }: updateUserAttributeInput): Observable<boolean> {
    return from(updateUserAttribute({
      userAttribute: {
        attributeKey,
        value,
      }
    })
    ).pipe(
      map(res => {
        if (res.nextStep.updateAttributeStep === 'DONE') {
          return true;
        } else {
          // NEXT STEP HAS TO BE HANDLED
          return false;
        }
      })
    )
  }

  /**
   * @description gets username and userId. No network call (from session storage?)
   */
  private getCurrentUser(): Observable<{ user?: AuthUser, error?: string }> {
    return from(getCurrentUser())
      .pipe(
        catchError((error: any) => {
          let response = 'UNKNOWN_ERROR';
          if (error.message === 'User needs to be authenticated to call this API.') {
            response = 'NOT_AUTHENTICATED';
          }
          return of(response);
        }),
        map((res: AuthUser | string) => {
          if (typeof (res) === 'string') {
            return {
              error: res
            }
          } else {
            return {
              user: res
            }
          }
        })
      )
  }
}


type loginInput = {
  username: string;
  password?: string;
  options?: SignInInput['options'];
};

type updateUserAttributeInput = {
  attributeKey: string;
  value: string;
}

